// Sugalarm — home-screen glucose widget bridge dispatcher (cycle 212).
//
// One small per-platform dispatcher so the reading-driven effect in
// App.tsx writes the widget snapshot with a single call regardless of
// surface:
//   - iOS  → the cycle-211 LiveActivityBridge.writeWidgetSnapshot (App
//            Group snapshot read by SugalarmWidgetsExtension);
//   - Android `standard` → the cycle-212 WidgetBridge plugin
//            (SharedPreferences snapshot read by GlucoseAppWidgetProvider).
//
// The snapshot SHAPE is identical on both platforms (the snake_case
// payload built by `buildWidgetSnapshotPayload` in liveActivity.ts), so
// the iOS and Android widgets are seeded from the same data and read
// identically. Reached EXCLUSIVELY via `window.Capacitor.Plugins`
// (cycle-138 rule) — inert everywhere off the native shells: web, the Pi
// kiosk, the Android `kiosk` flavor (which never compiles WidgetBridge),
// and PWA all no-op.
//
// DISPLAY-ONLY: a glance snapshot, never an alarm control (cycle-73).

import { capacitorPlatform, isCapacitorNative } from "./capacitor";
import {
  buildWidgetSnapshotPayload,
  clearGlucoseWidgetSnapshot as clearIosWidgetSnapshot,
  writeGlucoseWidgetSnapshot as writeIosWidgetSnapshot,
  type LiveActivitySeed,
} from "./liveActivity";

interface WidgetBridgePlugin {
  writeSnapshot: (opts: Record<string, unknown>) => Promise<void>;
  clear: () => Promise<void>;
}

/** Resolve the Android `standard`-flavor WidgetBridge plugin off the
 *  Capacitor bridge. Null off the Android native shell (and on the kiosk
 *  flavor, which never registers it). */
function androidWidgetBridge(): WidgetBridgePlugin | null {
  if (!isCapacitorNative() || capacitorPlatform() !== "android") return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.["WidgetBridge"];
  return p != null ? (p as WidgetBridgePlugin) : null;
}

/** Write the latest reading to the home-screen widget snapshot for the
 *  active platform. No-ops off iOS / Android `standard`. */
export async function writeGlucoseWidgetSnapshot(
  seed: LiveActivitySeed,
): Promise<void> {
  const platform = capacitorPlatform();
  if (platform === "ios") {
    await writeIosWidgetSnapshot(seed);
    return;
  }
  if (platform === "android") {
    const bridge = androidWidgetBridge();
    if (!bridge) return;
    try {
      await bridge.writeSnapshot(buildWidgetSnapshotPayload(seed));
    } catch (err) {
      console.warn("[widget] android snapshot write failed", err);
    }
  }
}

/** Write the signed-out placeholder (sign-out). The widget shows
 *  "Open Sugalarm" instead of a stale number. */
export async function clearGlucoseWidgetSnapshot(): Promise<void> {
  const platform = capacitorPlatform();
  if (platform === "ios") {
    await clearIosWidgetSnapshot();
    return;
  }
  if (platform === "android") {
    const bridge = androidWidgetBridge();
    if (!bridge) return;
    try {
      await bridge.clear();
    } catch {
      /* best-effort */
    }
  }
}
